import { ReactKeycloakProvider } from '@react-keycloak/web';
import { ReactNode, useMemo } from 'react';

import { Loader } from '@/components/Loader/Loader';
import { changeAppLanguage, getStoredAppLanguage } from '@/i18n/config';
import keycloak from '@/lib/keycloak';
import { Language, Locale } from '@/types';
import { getLocaleFromString } from '@/utils/i18n.utils';

export const options = {
  onLoad: 'login-required',
  // onLoad: 'check-sso', // TODO: Only for dev mode
  checkLoginIframe: false,
};

export interface AuthProviderProps {
  children: ReactNode;
}

const isDevelopment = () => import.meta.env.MODE === 'development';

const TOKENS_KEY = 'kcTokens';

const setTokens = (tokens: unknown) => {
  if (!isDevelopment()) {
    return;
  }
  localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
};

const getTokens = () => {
  return isDevelopment() ? JSON.parse(localStorage.getItem(TOKENS_KEY) || '{}') : {};
};

function getLocaleFromLanguage(language: Language): Locale {
  switch (language) {
    case 'es':
      return 'es_ES';

    case 'en':
      return 'en_US';

    case 'ca':
      return 'ca_CA';

    case 'eu':
      return 'eu_EU';
    default:
      return 'es_ES';
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const onKeycloakEvents = (event: string) => {
    if (event === 'onTokenExpired' || event === 'onInitError') {
      setTokens(null);
    }
    if (event === 'onAuthSuccess') {
      const storedLanguage = getStoredAppLanguage();
      if (storedLanguage) {
        changeAppLanguage(storedLanguage);
        return;
      }
      const tokenLanguage = keycloak.tokenParsed?.locale;
      if (tokenLanguage) {
        const locale = getLocaleFromLanguage(tokenLanguage);
        changeAppLanguage(locale);
        return;
      }
      const browserLanguage = navigator.language;
      if (browserLanguage) {
        const language = getLocaleFromString(browserLanguage.replace('-', '_'));

        if (language) {
          changeAppLanguage(language);
        }
      }
    }
  };

  const initOptions = useMemo(() => {
    return {
      ...options,
      ...getTokens(),
    };
  }, [options]);

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={initOptions}
      onEvent={onKeycloakEvents}
      onTokens={setTokens}
      LoadingComponent={
        <div className="flex h-screen w-screen items-center justify-center">
          <Loader />
        </div>
      }
    >
      {children}
    </ReactKeycloakProvider>
  );
}
